import type { MouseEvent } from 'react';
import { lazy, Suspense, useState } from 'react';

import BpkButton, { BUTTON_TYPES } from '@skyscanner-internal/backpack-web/bpk-component-button';
import observer from 'saddlebag-observer';

import handleClickAndTrack from '../utils/handleClickAndTrack';

import type { CultureSelectorEmbeddedProps } from '../types';
import type { OnCultureSelectorButtonClick } from '@web-platform/global-components-culture-selector/helpers/useCultureSelectorData';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './CultureSelectorButton.module.scss';

const LazyCultureSelector = lazy(() => import('@web-platform/global-components-culture-selector'));

type Props = {
  cultureSelectorProps?: CultureSelectorEmbeddedProps;
  currency: string;
  currencyLabel: string;
  isFetching?: boolean;
  locale: string;
  localeName: string;
  logger: Logger;
  market: string;
  marketName: string;
  trackEventFn: TrackingEventFn;
  onCultureSelectorButtonClick: OnCultureSelectorButtonClick;
};

const CultureSelectorButton = (props: Props) => {
  // This value helps to ensure the culture selector JS is fetched as soon as intent has been shown to open
  const [hasCultureButtonBeenClicked, setHasCultureButtonBeenClicked] = useState(false);
  const [isCultureSelectorVisible, setCultureSelectorVisible] = useState(false);

  const {
    cultureSelectorProps,
    currency,
    currencyLabel,
    isFetching,
    locale,
    localeName,
    logger,
    market,
    marketName,
    onCultureSelectorButtonClick,
    trackEventFn,
  } = props;

  const clickHandler = async (e: MouseEvent<HTMLButtonElement>) => {
    setHasCultureButtonBeenClicked(true);

    props.logger.logOperationalEvent({
      eventName: 'Culture Selector Button Clicked',
      component: 'header',
    });
    handleClickAndTrack(e, 'cultureInfo', 'Clicked', logger, trackEventFn);
    observer.publish('culture-button-clicked', {
      market,
      locale,
      currency,
    });
    observer.publish('culture-selector-clicked', {
      market,
      locale,
      currency,
    });

    onCultureSelectorButtonClick()
      .then(() => {
        setCultureSelectorVisible(true);
      })
      .catch((error) => {
        logger.logError(error);
        setCultureSelectorVisible(false);
      });
  };

  const flag = (
    <img
      className={STYLES['CultureSelectorButton__country-flag']}
      src={`https://images.skyscnr.com/images/country/flag/header/${market.toLowerCase()}.png`}
      alt={market}
      title={market}
    />
  );
  const closeCultureSelector = () => {
    setCultureSelectorVisible(false);
  };
  const getLanguage = (isMobileViewPort: boolean) =>
    isMobileViewPort ? locale.toUpperCase().substring(0, 2) : localeName;

  const getMobileCurrencyLabel = (isMobileViewPort: boolean) => {
    const currencySegments = currencyLabel.split(' ');
    const unique = Array.from(new Set(currencySegments));
    if (!isMobileViewPort) {
      return unique.join(' ');
    }
    if (!unique[1]) {
      return `${unique[0]}`;
    }
    return `${unique[1]} (${unique[0]})`;
  };

  const isCultureSelectorModalOpen = Boolean(
    hasCultureButtonBeenClicked &&
    cultureSelectorProps?.locales?.length &&
    isCultureSelectorVisible &&
    !isFetching,
  );

  const cultureSelector =
    hasCultureButtonBeenClicked && cultureSelectorProps ? (
      <Suspense fallback={null}>
        <LazyCultureSelector
          onClose={closeCultureSelector}
          isOpen={isCultureSelectorModalOpen}
          logger={logger}
          currency={currency}
          locale={locale}
          market={market}
          trackEventFn={trackEventFn}
          {...cultureSelectorProps}
        />
      </Suspense>
    ) : undefined;

  return (
    <BpkButton
      className={STYLES.CultureSelectorButton}
      onClick={clickHandler}
      type={BUTTON_TYPES.secondaryOnDark}
      data-testid="header-culture-selector-button"
    >
      <div className={STYLES.CultureSelectorButton__labels}>
        <span className={STYLES['CultureSelectorButton__locale--desktop']}>
          {getLanguage(false)}
        </span>
        <span className={STYLES['CultureSelectorButton__locale--mobile']}>{getLanguage(true)}</span>
        {flag}
        <span className={STYLES.CultureSelectorButton__marketNameLabels}>{marketName}</span>
        <span className={STYLES['CultureSelectorButton__currency-label--desktop']}>
          {getMobileCurrencyLabel(false)}
        </span>
        <span className={STYLES['CultureSelectorButton__currency-label--mobile']}>
          {getMobileCurrencyLabel(true)}
        </span>
      </div>
      {cultureSelector}
    </BpkButton>
  );
};

export default CultureSelectorButton;
